import React, { useEffect, useState, useRef } from 'react';
import api, { STATIC_BASE, LOGO_URL, API_BASE } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader, Card, Loading, SectionBadge } from '@/components/UI';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/format';
import { Save, Upload as UploadIcon, Plus, Download, Trash2 } from 'lucide-react';

const TABS = [
  { key: 'ppt', label: 'PPT Configuration' },
  { key: 'units', label: 'Units' },
  { key: 'users', label: 'User Management' },
  { key: 'app', label: 'App Settings' },
  { key: 'backup', label: 'Backup & Restore' },
  { key: 'audit', label: 'Audit Log' },
];

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState('ppt');
  if (user.role !== 'admin') {
    return <Card title="Restricted"><div className="text-sm text-slate-500">Only administrators can access settings.</div></Card>;
  }
  return (
    <div className="space-y-6">
      <PageHeader title="Settings" subtitle="Application configuration and governance" />
      <div className="border-b border-[var(--aampl-border)] flex gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === t.key ? 'border-[var(--aampl-primary)] text-[var(--aampl-primary)]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}>{t.label}</button>
        ))}
      </div>
      {tab === 'ppt' && <PPTConfigTab />}
      {tab === 'units' && <UnitsTab />}
      {tab === 'users' && <UsersTab />}
      {tab === 'app' && <AppSettingsTab />}
      {tab === 'backup' && <BackupTab />}
      {tab === 'audit' && <AuditTab />}
    </div>
  );
}

function PPTConfigTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [logoBust, setLogoBust] = useState(Date.now());

  const refresh = async () => {
    setLoading(true);
    const r = await api.get('/settings/ppt');
    setCfg(r.data);
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  if (loading || !cfg) return <Loading />;

  const save = async () => {
    setSaving(true);
    try {
      const { id, _id, ...payload } = cfg;
      await api.put('/settings/ppt', payload);
      toast.success('PPT config saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post('/settings/ppt/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo updated');
      setLogoBust(Date.now());
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  const setColor = (k, v) => setCfg({ ...cfg, colors: { ...cfg.colors, [k]: v } });
  const setFont = (k, v) => setCfg({ ...cfg, fonts: { ...cfg.fonts, [k]: v } });
  const setSec = (k, v) => setCfg({ ...cfg, sections_enabled: { ...cfg.sections_enabled, [k]: v } });
  const setTitle = (k, v) => setCfg({ ...cfg, section_titles: { ...cfg.section_titles, [k]: v } });
  const setIso = (k, v) => setCfg({ ...cfg, iso_clause_mapping: { ...cfg.iso_clause_mapping, [k]: v } });

  return (
    <div className="space-y-6">
      <Card title="Company & Logo" action={<button className="btn-primary text-sm" onClick={save} disabled={saving}><Save size={14} className="inline mr-1" />{saving ? 'Saving…' : 'Save All'}</button>}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div><label className="aampl-label">Company Name</label><input className="aampl-input" value={cfg.company_name || ''} onChange={(e) => setCfg({ ...cfg, company_name: e.target.value })} /></div>
            <div><label className="aampl-label">Full Name</label><input className="aampl-input" value={cfg.company_full_name || ''} onChange={(e) => setCfg({ ...cfg, company_full_name: e.target.value })} /></div>
            <div><label className="aampl-label">Footer Text</label><input className="aampl-input" value={cfg.footer_text || ''} onChange={(e) => setCfg({ ...cfg, footer_text: e.target.value })} /></div>
            <div><label className="aampl-label">Confidential Text</label><input className="aampl-input" value={cfg.confidential_text || ''} onChange={(e) => setCfg({ ...cfg, confidential_text: e.target.value })} /></div>
          </div>
          <div className="flex flex-col items-center">
            <img src={`${LOGO_URL}?v=${logoBust}`} alt="Logo" className="w-24 h-24 object-contain bg-white border border-[var(--aampl-border)] rounded p-2" onError={(e)=>{e.currentTarget.style.display='none'}} />
            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
            <button className="btn-secondary text-xs mt-2" onClick={() => fileRef.current?.click()}><UploadIcon size={12} className="inline mr-1" /> Upload Logo</button>
          </div>
        </div>
      </Card>
      <Card title="Colors">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(cfg.colors || {}).map(([k, v]) => (
            <div key={k}><label className="aampl-label">{k.replace(/_/g, ' ')}</label>
              <div className="flex gap-2"><input className="aampl-input flex-1" value={v} onChange={(e) => setColor(k, e.target.value)} /><input type="color" className="w-10 h-10 border rounded" value={v} onChange={(e) => setColor(k, e.target.value)} /></div>
            </div>
          ))}
        </div>
      </Card>
      <Card title="Fonts">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="aampl-label">Heading Font</label><input className="aampl-input" value={cfg.fonts?.heading || ''} onChange={(e) => setFont('heading', e.target.value)} /></div>
          <div><label className="aampl-label">Body Font</label><input className="aampl-input" value={cfg.fonts?.body || ''} onChange={(e) => setFont('body', e.target.value)} /></div>
        </div>
      </Card>
      <Card title="Section Toggles">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {Object.entries(cfg.sections_enabled || {}).map(([k, v]) => (
            <label key={k} className="flex items-center gap-2 text-sm border border-[var(--aampl-border)] rounded p-2">
              <input type="checkbox" checked={!!v} onChange={(e) => setSec(k, e.target.checked)} /> {k.replace(/_/g, ' ')}
            </label>
          ))}
        </div>
      </Card>
      <Card title="Section Titles & ISO Clauses">
        <div className="space-y-2">
          {Object.keys(cfg.section_titles || {}).map((k) => (
            <div key={k} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
              <div className="text-xs uppercase text-slate-500">{k}</div>
              <input className="aampl-input" value={cfg.section_titles[k]} onChange={(e) => setTitle(k, e.target.value)} />
              <input className="aampl-input" value={cfg.iso_clause_mapping?.[k] || ''} onChange={(e) => setIso(k, e.target.value)} placeholder="ISO clause" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function UnitsTab() {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [unitMap, setUnitMap] = useState({});
  const [defaultMap, setDefaultMap] = useState({});
  const [savingMap, setSavingMap] = useState(false);

  const refresh = async () => {
    setLoading(true);
    const [u, sett, def] = await Promise.all([
      api.get('/units'),
      api.get('/settings/app'),
      api.get('/settings/unit-map/default'),
    ]);
    setUnits(u.data || []);
    setDefaultMap(def.data || {});
    const mapSetting = (sett.data || []).find((s) => s.key === 'unit_name_map');
    setUnitMap(mapSetting?.value || {});
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const saveMap = async () => {
    setSavingMap(true);
    try { await api.put('/settings/app/unit_name_map', { value: unitMap }); toast.success('Unit name map saved'); refresh(); }
    catch (e) { toast.error('Failed'); } finally { setSavingMap(false); }
  };
  const removeMapping = (k) => { const next = { ...unitMap }; delete next[k]; setUnitMap(next); };
  const addMapping = (name, code) => { if (!name || !code) return; setUnitMap({ ...unitMap, [name]: code }); };

  const deleteUnit = async (id) => { if (!window.confirm('Soft-delete this unit?')) return; try { await api.delete(`/units/${id}`); toast.success('Unit removed'); refresh(); } catch (e) { toast.error('Failed'); } };

  if (loading) return <Loading />;
  return (
    <div className="space-y-4">
      <Card title="AAMPL Units" action={<button className="btn-primary text-sm" onClick={() => setCreating(true)} data-testid="btn-add-unit"><Plus size={14} className="inline mr-1" /> Add Unit</button>}>
        <div className="aampl-table-wrap"><table className="aampl-table">
          <thead><tr><th>Code</th><th>Name</th><th>Full Name</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {units.map((u) => (
              <tr key={u.id}><td className="font-mono font-bold aampl-gold-text">{u.code}</td><td>{u.name}</td><td>{u.full_name || '-'}</td><td><SectionBadge status={u.is_active ? 'submitted' : 'overdue'} label={u.is_active ? 'Active' : 'Inactive'} /></td><td>{u.is_active && <button className="btn-ghost text-rose-600" onClick={() => deleteUnit(u.id)}><Trash2 size={14} /></button>}</td></tr>
            ))}
          </tbody>
        </table></div>
      </Card>
      <Card title="Unit Name Mapping (Source App → MRM Unit Code)" action={<button className="btn-primary text-sm" onClick={saveMap} disabled={savingMap}>{savingMap ? 'Saving…' : 'Save Overrides'}</button>}>
        <div className="text-xs text-slate-600 mb-3">Records in source app JSONs whose unit name doesn't match a recognized AAMPL unit are <b>silently excluded</b>. Use this mapping to teach the system about new aliases. Codes must be one of: U1, U3, SL.</div>
        <div className="space-y-2 mb-3">
          {Object.entries(unitMap).length === 0 ? <div className="text-sm text-slate-500">No overrides set — defaults will be used.</div> : Object.entries(unitMap).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-sm border border-[var(--aampl-border)] rounded p-2">
              <span className="font-mono flex-1">{k}</span> → <span className="font-mono font-bold aampl-gold-text">{v}</span>
              <button className="btn-ghost text-rose-600" onClick={() => removeMapping(k)}><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
        <AddMappingRow onAdd={addMapping} />
        <details className="mt-4 text-xs">
          <summary className="cursor-pointer font-semibold aampl-navy-text">View built-in defaults ({Object.keys(defaultMap).length} entries)</summary>
          <div className="mt-2 grid grid-cols-2 gap-1 font-mono">
            {Object.entries(defaultMap).map(([k, v]) => <div key={k}>{k} → <b>{v}</b></div>)}
          </div>
        </details>
      </Card>
      {creating && <CreateUnitModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); refresh(); }} />}
    </div>
  );
}

function AddMappingRow({ onAdd }) {
  const [k, setK] = useState(''); const [v, setV] = useState('U1');
  return (
    <div className="flex gap-2 items-end border-t border-[var(--aampl-border)] pt-3">
      <div className="flex-1"><label className="aampl-label">Source name</label><input className="aampl-input" value={k} onChange={(e) => setK(e.target.value)} placeholder="e.g. Unit-1 Faridabad" /></div>
      <div><label className="aampl-label">MRM Code</label><select className="aampl-input" style={{width: 90}} value={v} onChange={(e) => setV(e.target.value)}><option>U1</option><option>U3</option><option>SL</option></select></div>
      <button className="btn-secondary" onClick={() => { onAdd(k, v); setK(''); }} data-testid="btn-add-mapping"><Plus size={14} /> Add</button>
    </div>
  );
}

function CreateUnitModal({ onClose, onDone }) {
  const [d, setD] = useState({ code: '', name: '', full_name: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => { setBusy(true); try { await api.post('/units', d); toast.success('Unit created'); onDone(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); } };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold aampl-navy-text mb-4">New Unit</h3>
        <div className="space-y-3">
          <div><label className="aampl-label">Code *</label><input className="aampl-input" value={d.code} onChange={(e) => setD({...d, code: e.target.value.toUpperCase()})} placeholder="e.g. U4" /></div>
          <div><label className="aampl-label">Name *</label><input className="aampl-input" value={d.name} onChange={(e) => setD({...d, name: e.target.value})} /></div>
          <div><label className="aampl-label">Full Name</label><input className="aampl-input" value={d.full_name} onChange={(e) => setD({...d, full_name: e.target.value})} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-4"><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={submit} disabled={busy}>{busy ? 'Creating…' : 'Create'}</button></div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [units, setUnits] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    setLoading(true);
    const [u, c, un] = await Promise.all([api.get('/users'), api.get('/users/sections'), api.get('/units')]);
    setUsers(u.data || []); setCatalogue(c.data || []); setUnits(un.data || []); setLoading(false);
  };
  useEffect(() => { refresh(); }, []);
  const labelOf = (k) => (catalogue.find((c) => c.key === k) || {}).label || k;
  const remove = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name} (${u.email})?\n\nThis cannot be undone. The account is removed and they can no longer sign in.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success('User deleted'); refresh(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  if (loading) return <Loading />;
  return (
    <div className="space-y-4">
      <Card title="User Management" action={<button className="btn-primary text-sm" onClick={() => setCreating(true)} data-testid="btn-add-user"><Plus size={14} className="inline mr-1" /> Add User</button>}>
        <div className="text-xs text-slate-500 mb-3">A user sees and works with everything that belongs to the sections ticked for them. Admin holds all sections and User Management; Management holds all sections read-only.</div>
        <div className="aampl-table-wrap">
          <table className="aampl-table">
            <thead><tr><th>Name</th><th>Username / Email</th><th>Role</th><th>Section Access</th><th>Units</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`}>
                  <td className="font-medium">{u.name}</td>
                  <td className="font-mono text-xs">{u.email}</td>
                  <td><span className="text-xs uppercase font-semibold">{u.role}</span></td>
                  <td>
                    {u.all_sections ? <span className="section-badge" style={{ background: '#EEF3FF', color: '#3730A3', borderColor: '#C7D2FE' }}>All sections{u.role === 'management' ? ' (read)' : ''}</span>
                      : (u.sections || []).length === 0 ? <span className="text-xs text-slate-400">None</span>
                      : <div className="flex flex-wrap gap-1">{(u.sections || []).map((k) => <span key={k} className="section-badge" style={{ background: '#F3EEFF', color: '#6D28D9', borderColor: '#DDD6FE' }}>{labelOf(k)}</span>)}</div>}
                  </td>
                  <td className="text-xs">{u.all_units ? 'All' : (u.unit_ids || []).map((id) => (units.find((x) => x.id === id) || {}).code || '?').join(', ') || '—'}</td>
                  <td><SectionBadge status={u.is_active ? 'submitted' : 'overdue'} label={u.is_active ? 'Active' : 'Inactive'} /></td>
                  <td className="whitespace-nowrap">
                    <button className="btn-ghost text-xs" onClick={() => setEditing(u)} data-testid={`btn-edit-access-${u.email}`}>Edit Access</button>
                    <button className="btn-ghost text-xs" onClick={() => setResetting(u)}>Reset Password</button>
                    <button className="btn-ghost text-xs text-rose-600" onClick={() => remove(u)} data-testid={`btn-delete-user-${u.email}`}><Trash2 size={13} /> Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {creating && <UserModal catalogue={catalogue} units={units} onClose={() => setCreating(false)} onDone={() => { setCreating(false); refresh(); }} />}
      {editing && <UserModal catalogue={catalogue} units={units} existing={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); refresh(); }} />}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} onDone={() => { setResetting(null); refresh(); }} />}
    </div>
  );
}

function SectionPicker({ catalogue, value, onChange, disabled }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="section-picker">
      {catalogue.map((s) => (
        <label key={s.key} className={`flex items-start gap-2 text-sm cursor-pointer rounded-xl border px-3 py-2 transition ${value.includes(s.key) ? 'border-[#C7D2FE] bg-[#EEF3FF]' : 'border-[var(--aampl-border)] bg-white hover:bg-slate-50'} ${disabled ? 'opacity-50 pointer-events-none' : ''}`} title={(s.includes || []).join(' • ')}>
          <input type="checkbox" className="mt-0.5" checked={value.includes(s.key)} onChange={() => toggle(s.key)} data-testid={`section-${s.key}`} />
          <span><span className="font-medium">{s.label}</span><span className="block text-[11px] text-slate-500 leading-tight">{(s.includes || []).slice(0, 3).join(' • ')}</span></span>
        </label>
      ))}
    </div>
  );
}

function UserModal({ catalogue, units, existing, onClose, onDone }) {
  const isEdit = !!existing;
  const [d, setD] = useState(existing
    ? { name: existing.name || '', email: existing.email || '', password: '', role: existing.role || 'user', sections: existing.sections || [], all_units: !!existing.all_units, unit_ids: existing.unit_ids || [], is_active: existing.is_active !== false }
    : { name: '', email: '', password: '', role: 'user', sections: [], all_units: true, unit_ids: [], is_active: true });
  const [busy, setBusy] = useState(false);
  const fullAccess = d.role === 'admin' || d.role === 'management';
  useEffect(() => { if (fullAccess) setD((p) => ({ ...p, all_units: true })); }, [fullAccess]);
  const toggleUnit = (uid) => setD({ ...d, unit_ids: d.unit_ids.includes(uid) ? d.unit_ids.filter((x) => x !== uid) : [...d.unit_ids, uid] });
  const submit = async () => {
    if (!d.name.trim() || !d.email.trim()) { toast.error('Name and username / email are required'); return; }
    if (!isEdit && d.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (!fullAccess && d.sections.length === 0) { toast.error('Tick at least one section'); return; }
    setBusy(true);
    try {
      if (isEdit) {
        const body = { name: d.name, role: d.role, sections: fullAccess ? [] : d.sections, all_units: d.all_units, unit_ids: d.all_units ? [] : d.unit_ids, is_active: d.is_active };
        if (d.password) body.password = d.password;
        await api.put(`/users/${existing.id}`, body); toast.success('Access updated');
      } else {
        await api.post('/users', { name: d.name, email: d.email, password: d.password, role: d.role, sections: fullAccess ? [] : d.sections, all_units: d.all_units, unit_ids: d.all_units ? [] : d.unit_ids });
        toast.success('User created');
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="eyebrow mb-1">User Management</div>
        <h3 className="text-xl font-extrabold tracking-tight text-[var(--aampl-text)] mb-4">{isEdit ? `Edit Access — ${existing.name}` : 'Add User'}</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="aampl-label">Name</label><input className="aampl-input" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} data-testid="new-user-name" placeholder="e.g. Anju" /></div>
            <div><label className="aampl-label">Username / Email</label><input type="email" className="aampl-input" value={d.email} disabled={isEdit} onChange={(e) => setD({ ...d, email: e.target.value })} data-testid="new-user-email" placeholder="anju@aampl.in" /></div>
            <div><label className="aampl-label">{isEdit ? 'New password (leave blank to keep)' : 'Password / Temporary password'}</label><input type="password" className="aampl-input" value={d.password} onChange={(e) => setD({ ...d, password: e.target.value })} data-testid="new-user-password" autoComplete="new-password" /></div>
            <div><label className="aampl-label">Role</label>
              <select className="aampl-input" value={d.role} onChange={(e) => setD({ ...d, role: e.target.value })} data-testid="new-user-role">
                <option value="user">User (section access)</option>
                <option value="management">Management (all sections, read-only, PPT)</option>
                <option value="admin">Admin (everything + User Management)</option>
              </select>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="aampl-label mb-0">Section Access</label>
              {!fullAccess && <div className="flex gap-2 text-[11px]"><button type="button" className="text-[var(--aampl-primary)] font-semibold" onClick={() => setD({ ...d, sections: catalogue.map((c) => c.key) })}>Select all</button><button type="button" className="text-slate-500 font-semibold" onClick={() => setD({ ...d, sections: [] })}>Clear</button></div>}
            </div>
            {fullAccess ? <div className="text-sm rounded-xl border border-[#C7D2FE] bg-[#EEF3FF] px-3 py-2 text-[#3730A3]">This role holds <b>all sections</b> automatically{d.role === 'management' ? ' (read-only; can generate the PPT)' : ''}.</div>
              : <SectionPicker catalogue={catalogue} value={d.sections} onChange={(v) => setD({ ...d, sections: v })} />}
          </div>
          <div className="border-t border-[var(--aampl-border)] pt-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={d.all_units} disabled={fullAccess} onChange={(e) => setD({ ...d, all_units: e.target.checked })} /> Access all units</label>
            {!d.all_units && (
              <div className="mt-2 flex flex-wrap gap-3">
                {units.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={d.unit_ids.includes(u.id)} onChange={() => toggleUnit(u.id)} /> {u.name} <span className="text-[10px] text-[var(--aampl-primary)] font-bold">({u.code})</span></label>
                ))}
              </div>
            )}
          </div>
          {isEdit && <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={d.is_active} onChange={(e) => setD({ ...d, is_active: e.target.checked })} /> Active (can sign in)</label>}
        </div>
        <div className="flex justify-end gap-2 mt-5"><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={submit} disabled={busy} data-testid="btn-create-user">{busy ? 'Saving…' : isEdit ? 'Save Access' : 'Create User'}</button></div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ user: target, onClose, onDone }) {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (pw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setBusy(true);
    try { await api.post(`/users/${target.id}/reset-password`, { password: pw }); toast.success('Password reset'); onDone(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-extrabold tracking-tight text-[var(--aampl-text)] mb-1">Reset password</h3>
        <div className="text-sm text-slate-500 mb-4">{target.name} · {target.email}</div>
        <label className="aampl-label">New temporary password</label>
        <input type="password" className="aampl-input" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" />
        <div className="flex justify-end gap-2 mt-5"><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Reset'}</button></div>
      </div>
    </div>
  );
}

function AppSettingsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = async () => { setLoading(true); const r = await api.get('/settings/app'); setItems(r.data || []); setLoading(false); };
  useEffect(() => { refresh(); }, []);
  const update = async (key, value) => { try { await api.put(`/settings/app/${key}`, { value }); toast.success('Updated'); refresh(); } catch (e) { toast.error('Failed'); } };
  if (loading) return <Loading />;
  return (
    <Card title="App Settings">
      <div className="space-y-3">
        {items.map((s) => (
          <SettingRow key={s.key} setting={s} onUpdate={update} />
        ))}
      </div>
    </Card>
  );
}
function SettingRow({ setting, onUpdate }) {
  const [val, setVal] = useState(setting.value);
  const isBool = typeof setting.value === 'boolean';
  return (
    <div className="flex items-center justify-between border border-[var(--aampl-border)] rounded p-3">
      <div><div className="text-sm font-semibold">{setting.key.replace(/_/g, ' ')}</div><div className="text-xs text-slate-500">{setting.description}</div></div>
      <div className="flex items-center gap-2">
        {isBool ? <input type="checkbox" checked={val} onChange={(e) => setVal(e.target.checked)} /> : <input className="aampl-input" style={{width:'120px'}} value={val} onChange={(e) => setVal(e.target.value)} />}
        <button className="btn-primary text-xs" onClick={() => onUpdate(setting.key, isBool ? val : (isNaN(Number(val)) ? val : Number(val)))}>Save</button>
      </div>
    </div>
  );
}

function ScheduleCard() {
  const [cfg, setCfg] = useState({ enabled: false, hour: 2, minute: 0, retention: 30, last_run_at: null, last_run_status: null, last_run_message: null });
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const r = await api.get('/backup/schedule'); setCfg((c) => ({ ...c, ...r.data })); } catch (_) {} };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put('/backup/schedule', {
        enabled: cfg.enabled, hour: Number(cfg.hour), minute: Number(cfg.minute), retention: Number(cfg.retention),
      });
      setCfg((c) => ({ ...c, ...r.data }));
      toast.success('Schedule saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post('/backup/run-scheduled-now');
      toast.success(`Backup created — ${r.data.total_records} records, pruned ${r.data.pruned}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  const hhmm = `${String(cfg.hour).padStart(2, '0')}:${String(cfg.minute).padStart(2, '0')}`;

  return (
    <Card title="Scheduled Auto-Backup" action={<button className="btn-ghost text-sm" onClick={runNow} disabled={busy} data-testid="btn-run-backup-now">Run now</button>}>
      <div className="flex flex-wrap items-end gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="chk-schedule-enabled" /> Enabled
        </label>
        <div>
          <label className="aampl-label">Daily time (IST)</label>
          <input type="time" className="aampl-input" value={hhmm} data-testid="input-schedule-time"
            onChange={(e) => { const [h, m] = e.target.value.split(':'); setCfg({ ...cfg, hour: Number(h), minute: Number(m) }); }} />
        </div>
        <div>
          <label className="aampl-label">Keep last (scheduled)</label>
          <input type="number" min="1" className="aampl-input w-24" value={cfg.retention} data-testid="input-schedule-retention"
            onChange={(e) => setCfg({ ...cfg, retention: e.target.value })} />
        </div>
        <button className="btn-primary text-sm" onClick={save} disabled={busy} data-testid="btn-save-schedule">{busy ? 'Saving…' : 'Save schedule'}</button>
      </div>
      <div className="text-xs text-slate-500 mt-3" data-testid="schedule-last-run-status">
        {cfg.last_run_at
          ? <>Last run: {new Date(cfg.last_run_at).toLocaleString()} — <span className={cfg.last_run_status === 'success' ? 'text-emerald-600' : 'text-rose-600'}>{cfg.last_run_status}</span>{cfg.last_run_message ? ` (${cfg.last_run_message})` : ''}</>
          : 'No scheduled backup has run yet.'}
      </div>
    </Card>
  );
}

function BackupTab() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [includeUsers, setIncludeUsers] = useState(false);
  const fileRef = useRef(null);
  const refresh = async () => { const r = await api.get('/backup/list'); setItems(r.data || []); };
  useEffect(() => { refresh(); }, []);
  const create = async () => { setBusy(true); try { await api.post('/backup/create'); toast.success('Backup created'); refresh(); } catch (e) { toast.error('Failed'); } finally { setBusy(false); } };
  const download = async (id, fname) => {
    const token = localStorage.getItem('mrm_token');
    const resp = await fetch(`${API_BASE}/backup/download/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const link = document.createElement('a'); link.href = window.URL.createObjectURL(blob); link.download = fname; link.click();
  };
  const restore = async (file) => {
    if (!file) return;
    if (!window.confirm('Restore will overwrite existing data. Are you sure?')) return;
    setBusy(true);
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api.post(`/backup/restore?include_users=${includeUsers}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success(`Restored: ${JSON.stringify(r.data.restored)}`); refresh(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <ScheduleCard />
      <Card title="Backup" action={<button className="btn-primary text-sm" onClick={create} disabled={busy} data-testid="btn-create-backup">{busy ? 'Working…' : 'Create Backup'}</button>}>
        <div className="aampl-table-wrap">
          <table className="aampl-table">
            <thead><tr><th>Filename</th><th>Records</th><th>Size</th><th>Type</th><th>Date</th><th></th></tr></thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={6} className="text-center text-slate-500 py-4">No backups yet.</td></tr> : items.map((b) => (
                <tr key={b.id}><td className="font-mono text-xs">{b.filename}</td><td>{b.total_records?.toLocaleString()}</td><td>{(b.file_size_bytes / 1024).toFixed(1)} KB</td><td className="uppercase text-xs">{b.type}</td><td>{formatDateTime(b.created_at)}</td><td><button className="btn-ghost text-sm" onClick={() => download(b.id, b.filename)}><Download size={14} /></button></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Card title="Restore">
        <div className="text-sm text-slate-600 mb-3">Upload a previously downloaded backup JSON. <span className="text-rose-600 font-semibold">This will overwrite existing data.</span></div>
        <label className="flex items-center gap-2 mb-3 text-sm"><input type="checkbox" checked={includeUsers} onChange={(e) => setIncludeUsers(e.target.checked)} /> Include users in restore (otherwise existing users are kept)</label>
        <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={(e) => restore(e.target.files?.[0])} />
        <button className="btn-danger" onClick={() => fileRef.current?.click()} disabled={busy}>Choose backup file…</button>
      </Card>
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const refresh = async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (filterAction) q.append('action', filterAction);
    if (filterModule) q.append('module', filterModule);
    const r = await api.get(`/audit/logs?${q.toString()}`);
    setLogs(r.data || []); setLoading(false);
  };
  // eslint-disable-next-line
  useEffect(() => { refresh(); }, [filterAction, filterModule]);
  const exportCsv = async () => {
    const token = localStorage.getItem('mrm_token');
    const resp = await fetch(`${API_BASE}/audit/export`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const link = document.createElement('a'); link.href = window.URL.createObjectURL(blob); link.download = 'audit_logs.csv'; link.click();
  };
  return (
    <Card title="Audit Log" action={<button className="btn-secondary text-sm" onClick={exportCsv}><Download size={14} className="inline mr-1" /> Export CSV</button>}>
      <div className="flex gap-2 mb-3">
        <select className="aampl-input" style={{width:'auto'}} value={filterAction} onChange={(e) => setFilterAction(e.target.value)}><option value="">All actions</option><option value="created">Created</option><option value="updated">Updated</option><option value="deleted">Deleted</option><option value="submitted">Submitted</option><option value="locked">Locked</option><option value="generated_ppt">Generated PPT</option><option value="login">Login</option><option value="uploaded">Uploaded</option></select>
        <select className="aampl-input" style={{width:'auto'}} value={filterModule} onChange={(e) => setFilterModule(e.target.value)}><option value="">All modules</option><option value="auth">Auth</option><option value="mrm_cycle">Cycle</option><option value="manual_entry">Manual Entry</option><option value="action_item">Action Item</option><option value="data_upload">Upload</option><option value="ppt">PPT</option><option value="user">User</option><option value="backup">Backup</option><option value="settings">Settings</option></select>
      </div>
      {loading ? <Loading /> : (
        <div className="aampl-table-wrap" style={{maxHeight: '500px'}}>
          <table className="aampl-table">
            <thead><tr><th>Time</th><th>User</th><th>Role</th><th>Action</th><th>Module</th><th>Target</th><th>Details</th></tr></thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id}><td className="text-xs">{formatDateTime(l.timestamp)}</td><td>{l.user_name}</td><td className="uppercase text-xs">{l.user_role}</td><td className="uppercase text-xs font-semibold">{l.action}</td><td className="text-xs">{l.module}</td><td className="text-xs">{l.target_name}</td><td className="text-xs">{l.details}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
